import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IRestaurantFoodGroup, isRestaurantOpen } from "../services/search";
import { S3_IMAGE_BASE_URL } from "../services/config";
import { useLanguage } from "../context/LanguageContext";
import "../styles/style.css";

/**
 * Arama sonuçlarında (metin ya da kategori) tek bir restoranın sonucu: üstte logo ve ad, altında
 * eşleşen yemekleri resimleriyle yatay kaydırılan bir şerit. Kapalı restoran
 * soluk görünür ve "Kapalı" etiketi taşır. Masaüstünde
 * şeridin iki yanında kaydırma okları çıkar.
 */
function RestaurantFoodGroup({ group }: { group: IRestaurantFoodGroup }) {
  const { restaurant, foods } = group;
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const trackRef = useRef<HTMLDivElement>(null);

  const isOpen = isRestaurantOpen(restaurant);
  const reopensAt = !isOpen && restaurant.reopensAtUtc
    ? new Date(restaurant.reopensAtUtc).toLocaleTimeString(lang === "en" ? "en-GB" : "tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const openRestaurant = () => navigate(`/restaurants/${restaurant.id}`);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <section className={`category-group${isOpen ? "" : " category-group--closed"}`}>
      <button type="button" className="category-group__header" onClick={openRestaurant}>
        <span className="category-group__logo">
          {restaurant.restourantLogoPath ? (
            <img src={S3_IMAGE_BASE_URL + restaurant.restourantLogoPath} alt="" />
          ) : (
            <i className="bi bi-shop" />
          )}
        </span>
        <span className="category-group__info">
          <span className="category-group__title">
            <span className="category-group__name">{restaurant.restourantName}</span>
            {!isOpen && (
              <span className="category-group__closed-badge">
                <i className="bi bi-moon-fill me-1" />
                {t("restaurants.closed")}
              </span>
            )}
          </span>
          {!isOpen && (reopensAt || restaurant.closedNote) && (
            <span className="category-group__closed-note">
              {reopensAt ? t("restaurants.opensAt", { time: reopensAt }) : restaurant.closedNote}
            </span>
          )}
          <span className="category-group__meta">
            {restaurant.restourantEstDeliveryTime != null && (
              <span>
                <i className="bi bi-clock me-1" />
                {restaurant.restourantEstDeliveryTime} {t("common.minutes")}
              </span>
            )}
            {!!restaurant.restourantTotalFeedBackPoint && (
              <span>
                <i className="bi bi-star-fill me-1" style={{ color: "#f5a623" }} />
                {restaurant.restourantTotalFeedBackPoint.toFixed(1)}
              </span>
            )}
            <span>
              <i className="bi bi-bag me-1" />
              Min. {restaurant.restourantMinAmount.toFixed(0)}₺
            </span>
          </span>
        </span>
        <i className="bi bi-chevron-right category-group__chevron" />
      </button>

      <div className="category-group__rail">
        <button
          type="button"
          className="category-group__arrow category-group__arrow--prev"
          onClick={() => scrollBy(-1)}
          aria-label={t("categories.scrollPrev")}
        >
          <i className="bi bi-chevron-left" />
        </button>

        <div className="category-group__track" ref={trackRef}>
          {foods.map((food) => {
            const name = lang === "en" ? food.foodNameEn : food.foodNameTr;
            const currency = food.currency?.currencyDisplayText ?? "";
            const discounted = food.hasDiscount && food.discountedPriceText;
            return (
              <button key={food.id} type="button" className="category-food-card" onClick={openRestaurant}>
                {food.foodImagePath ? (
                  <img src={S3_IMAGE_BASE_URL + food.foodImagePath} alt={name} loading="lazy" />
                ) : (
                  <span className="category-food-card__placeholder">
                    <i className="bi bi-egg-fried" />
                  </span>
                )}
                <span className="category-food-card__pill">
                  <span className="category-food-card__name">{name}</span>
                  <span className="category-food-card__price">
                    {discounted ? (
                      <>
                        <s>{food.foodPriceText}</s> {food.discountedPriceText} {currency}
                      </>
                    ) : (
                      <>
                        {food.foodPriceText} {currency}
                      </>
                    )}
                  </span>
                </span>
                {discounted && (
                  <span className="category-food-card__discount">
                    <i className="bi bi-percent" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="category-group__arrow category-group__arrow--next"
          onClick={() => scrollBy(1)}
          aria-label={t("categories.scrollNext")}
        >
          <i className="bi bi-chevron-right" />
        </button>
      </div>
    </section>
  );
}

export default RestaurantFoodGroup;
